import { Alert, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate, useParams } from "react-router";

import GaiaDeleteButton from "~/modules/app/components/GaiaDeleteButton";
import GaiaPageComponent from "~/modules/app/components/GaiaPageComponent";
import GaiaPageLayoutColumn from "~/modules/app/components/GaiaPageLayoutColumn";
import { RESEARCH_TYPE_PATH } from "~/modules/settings/constants";
import FormTemplateEditor from "~/modules/settings/shared/formTemplateEditor/components/FormTemplateEditor";
import { useSettingsFormTemplateEditorData } from "~/modules/settings/shared/formTemplateEditor/hooks/useSettingsFormTemplateEditorData";

import FunctionTypeForm, { FunctionTypeFormInputs } from "../../components/FunctionTypeForm";
import FunctionTypeObservationTypeListCard from "./components/FunctionTypeObservationTypeListCard";
import { useSettingsFunctionTypeData } from "./hooks/useSettingsFunctionTypeData";
import { SettingsFunctionTypeEditRoute_GetByIdDocument } from "./queries.generated";

export default function SettingsFunctionTypeEditRoute() {
    const { t } = useTranslation("functionType");

    const navigate = useNavigate();

    const { functionTypeId, researchTypeId } = useParams();
    if (!functionTypeId || !researchTypeId) throw new Error("invalid params");

    const { queryData, updateMutation, deleteMutation, addObservationTypeMutation, removeObservationTypeMutation } =
        useSettingsFunctionTypeData(functionTypeId);

    const {
        addFormTemplateQuestionMutation,
        updateFormTemplateQuestionMutation,
        deleteFormTemplateQuestionMutation,
        handleAddQuestion,
        handleUpdateQuestion,
        editQuestionError,
    } = useSettingsFormTemplateEditorData({
        refetchQueries: [SettingsFunctionTypeEditRoute_GetByIdDocument],
        formTemplateId: queryData.functionType?.formTemplate.id,
    });

    const handleSubmit = async (data: FunctionTypeFormInputs) => {
        await updateMutation.updateFunctionType(functionTypeId, data);
        navigate(
            generatePath(RESEARCH_TYPE_PATH, {
                researchTypeId,
            }),
        );
    };

    const handleDelete = async () => {
        await deleteMutation.deleteFunctionType(functionTypeId);
        navigate(
            generatePath(RESEARCH_TYPE_PATH, {
                researchTypeId,
            }),
        );
    };

    if (queryData.loading) {
        return <CircularProgress />;
    }

    return (
        <GaiaPageLayoutColumn>
            {queryData.functionType && (
                <>
                    <GaiaPageComponent title={t("settingsFunctionTypeEditRoute.title")}>
                        {queryData.error && <Alert severity="error">{queryData.error.message}</Alert>}
                        <FunctionTypeForm
                            initialData={queryData.functionType}
                            onSubmit={handleSubmit}
                            error={updateMutation.error}
                        />
                    </GaiaPageComponent>
                    <GaiaPageComponent title={t("settingsFunctionTypeEditRoute.formTemplateEditorTitle")}>
                        <FormTemplateEditor
                            formTemplateQuestions={queryData.functionType.formTemplate.questions}
                            onAddQuestion={handleAddQuestion}
                            onUpdateQuestion={handleUpdateQuestion}
                            onDeleteQuestion={async id =>
                                await deleteFormTemplateQuestionMutation.deleteFormTemplateQuestion(id)
                            }
                            isAddQuestionLoading={addFormTemplateQuestionMutation.loading}
                            isUpdateQuestionLoading={updateFormTemplateQuestionMutation.loading}
                            isDeleteQuestionLoading={deleteFormTemplateQuestionMutation.loading}
                            addQuestionError={addFormTemplateQuestionMutation.error}
                            editQuestionError={editQuestionError}
                        />
                    </GaiaPageComponent>
                    <GaiaPageComponent
                        title={t("settingsFunctionTypeEditRoute.functionTypeObservationTypeListTitle")}
                        titleVariant="h3"
                    >
                        <FunctionTypeObservationTypeListCard
                            functionTypeObservationTypes={queryData.functionType.observationTypes}
                            researchTypeId={researchTypeId}
                            functionTypeId={functionTypeId}
                            onAddObservationType={data =>
                                addObservationTypeMutation.addFunctionTypeObservationType({
                                    ecologicalFunctionTypeId: functionTypeId,
                                    observationTypeId: data.observationTypeId,
                                })
                            }
                            isAddObservationTypeLoading={addObservationTypeMutation.loading}
                            addObservationTypeError={addObservationTypeMutation.error}
                            onRemoveObservationType={observationTypeId =>
                                removeObservationTypeMutation.removeFunctionTypeObservationType({
                                    ecologicalFunctionTypeId: functionTypeId,
                                    observationTypeId,
                                })
                            }
                            isRemoveObservationTypeLoading={removeObservationTypeMutation.loading}
                            removeObservationTypeError={removeObservationTypeMutation.error}
                        />
                    </GaiaPageComponent>
                    {deleteMutation.error && <Alert severity="error">{deleteMutation.error.message}</Alert>}
                    <GaiaPageComponent title={t("settingsFunctionTypeEditRoute.dangerZone")} titleVariant="h3">
                        <GaiaDeleteButton
                            label={t("settingsFunctionTypeEditRoute.functionTypeDeleteBtn")}
                            onDelete={handleDelete}
                            isLoading={deleteMutation.loading}
                        />
                    </GaiaPageComponent>
                </>
            )}
        </GaiaPageLayoutColumn>
    );
}
